import Plotly from 'plotly.js-dist-min';
import { useEffect, useRef } from 'react';

export interface Figure {
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  frames: Plotly.Frame[] | null;
}

export interface PlotParams {
  data: Plotly.Data[];
  layout: Partial<Plotly.Layout>;
  frames?: Plotly.Frame[] | undefined;
  config?: Partial<Plotly.Config> | undefined;
  /**
   * Callback executed after plot is initialized.
   * @param figure Object with three keys corresponding to input props: data, layout and frames.
   * @param graphDiv Reference to the DOM node into which the figure was rendered.
   */
  onInitialized?:
    | ((figure: Readonly<Figure>, graphDiv: Readonly<HTMLElement>) => void)
    | undefined;
  /**
   * Callback executed when when a plot is updated due to new data or layout, or when user interacts with a plot.
   * @param figure Object with three keys corresponding to input props: data, layout and frames.
   * @param graphDiv Reference to the DOM node into which the figure was rendered.
   */
  onUpdate?:
    | ((figure: Readonly<Figure>, graphDiv: Readonly<HTMLElement>) => void)
    | undefined;
  /**
   * Callback executed when component unmounts, before Plotly.purge strips the graphDiv of all private attributes.
   * @param figure Object with three keys corresponding to input props: data, layout and frames.
   * @param graphDiv Reference to the DOM node into which the figure was rendered.
   */
  onPurge?:
    | ((figure: Readonly<Figure>, graphDiv: Readonly<HTMLElement>) => void)
    | undefined;
  /**
   * Callback executed when a plotly.js API method rejects
   * @param err Error
   */
  onError?: ((err: Readonly<Error>) => void) | undefined;
  /**
   * id assigned to the <div> into which the plot is rendered.
   */
  divId?: string | undefined;
  /**
   * applied to the <div> into which the plot is rendered
   */
  className?: string | undefined;
  /**
   * used to style the <div> into which the plot is rendered
   */
  style?: React.CSSProperties | undefined;
  /**
   * When true, adds a call to Plotly.Plot.resize() as a window.resize event handler
   */
  useResizeHandler?: boolean | undefined;

  onAfterExport?: (() => void) | undefined;
  onAfterPlot?: (() => void) | undefined;
  onAnimated?: (() => void) | undefined;
  onAnimatingFrame?:
    | ((event: Readonly<Plotly.FrameAnimationEvent>) => void)
    | undefined;
  onAnimationInterrupted?: (() => void) | undefined;
  onAutoSize?: (() => void) | undefined;
  onBeforeExport?: (() => void) | undefined;
  onBeforeHover?:
    | ((event: Readonly<Plotly.PlotMouseEvent>) => boolean)
    | undefined;
  onButtonClicked?:
    | ((event: Readonly<Plotly.ButtonClickEvent>) => void)
    | undefined;
  onClick?: ((event: Readonly<Plotly.PlotMouseEvent>) => void) | undefined;
  onClickAnnotation?:
    | ((event: Readonly<Plotly.ClickAnnotationEvent>) => void)
    | undefined;
  onDeselect?: (() => void) | undefined;
  onDoubleClick?: (() => void) | undefined;
  onFramework?: (() => void) | undefined;
  onHover?: ((event: Readonly<Plotly.PlotHoverEvent>) => void) | undefined;
  onLegendClick?:
    | ((event: Readonly<Plotly.LegendClickEvent>) => boolean)
    | undefined;
  onLegendDoubleClick?:
    | ((event: Readonly<Plotly.LegendClickEvent>) => boolean)
    | undefined;
  onRelayout?:
    | ((event: Readonly<Plotly.PlotRelayoutEvent>) => void)
    | undefined;
  onRestyle?: ((event: Readonly<Plotly.PlotRestyleEvent>) => void) | undefined;
  onRedraw?: (() => void) | undefined;
  onSelected?:
    | ((event: Readonly<Plotly.PlotSelectionEvent>) => void)
    | undefined;
  onSelecting?:
    | ((event: Readonly<Plotly.PlotSelectionEvent>) => void)
    | undefined;
  onSliderChange?:
    | ((event: Readonly<Plotly.SliderChangeEvent>) => void)
    | undefined;
  onSliderEnd?: ((event: Readonly<Plotly.SliderEndEvent>) => void) | undefined;
  onSliderStart?:
    | ((event: Readonly<Plotly.SliderStartEvent>) => void)
    | undefined;
  onTransitioning?: (() => void) | undefined;
  onTransitionInterrupted?: (() => void) | undefined;
  onUnhover?: ((event: Readonly<Plotly.PlotMouseEvent>) => void) | undefined;
}

const Plot = (props: PlotParams): JSX.Element => {
  const divRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const ref = divRef.current!;
    Plotly.react(ref, props.data, props.layout, props.config).then((figure) => {
      if (props.onInitialized)
        props.onInitialized(figure as unknown as Figure, ref);
      if (props.onAfterExport)
        figure.on('plotly_afterexport', props.onAfterExport);
      if (props.onAfterPlot) figure.on('plotly_afterplot', props.onAfterPlot);
      if (props.onAnimated) figure.on('plotly_animated', props.onAnimated);
      if (props.onAnimatingFrame)
        figure.on('plotly_animatingframe', props.onAnimatingFrame);
      if (props.onAnimationInterrupted)
        figure.on('plotly_animationinterrupted', props.onAnimationInterrupted);
      if (props.onAutoSize) figure.on('plotly_autosize', props.onAutoSize);
      if (props.onBeforeExport)
        figure.on('plotly_beforeexport', props.onBeforeExport);
      if (props.onClick) figure.on('plotly_click', props.onClick);
      if (props.onClickAnnotation)
        figure.on('plotly_clickannotation', props.onClickAnnotation);
      if (props.onDeselect) figure.on('plotly_deselect', props.onDeselect);
      if (props.onDoubleClick)
        figure.on('plotly_doubleclick', props.onDoubleClick);
      if (props.onFramework) figure.on('plotly_framework', props.onFramework);
      if (props.onHover) figure.on('plotly_hover', props.onHover);
      if (props.onLegendClick)
        figure.on('plotly_legendclick', props.onLegendClick);
      if (props.onLegendDoubleClick)
        figure.on('plotly_legenddoubleclick', props.onLegendDoubleClick);
      if (props.onRedraw) figure.on('plotly_redraw', props.onRedraw);
      if (props.onRelayout) figure.on('plotly_relayout', props.onRelayout);
      if (props.onRestyle) figure.on('plotly_restyle', props.onRestyle);
      if (props.onSelected) figure.on('plotly_selected', props.onSelected);
      if (props.onSelecting) figure.on('plotly_selecting', props.onSelecting);
      if (props.onSliderChange)
        figure.on('plotly_sliderchange', props.onSliderChange);
      if (props.onSliderEnd) figure.on('plotly_sliderend', props.onSliderEnd);
      if (props.onSliderStart)
        figure.on('plotly_sliderstart', props.onSliderStart);
      if (props.onTransitioning)
        figure.on('plotly_transitioning', props.onTransitioning);
      if (props.onTransitionInterrupted)
        figure.on(
          'plotly_transitioninterrupted',
          props.onTransitionInterrupted
        );
      if (props.onUnhover) figure.on('plotly_unhover', props.onUnhover);
    });
    return () => Plotly.purge(ref);
  }, [props.data, props.layout]);
  return (
    <div
      id={props.divId}
      className={props.className}
      style={props.style}
      ref={divRef}
    ></div>
  );
};

export default Plot;
